import React, { useState } from 'react';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
  onQuickView: (product: Product) => void;
  liveData?: {
    price: string;
    availability: string;
    soldByAmazon?: boolean | null;
  };
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart, onQuickView, liveData }) => {
  const [imgError, setImgError] = useState(false);
  const [loading, setLoading] = useState(true);

  const isOutOfStock = liveData?.availability
    ? liveData.availability.toLowerCase().includes('out of stock') || 
      liveData.availability.toLowerCase().includes('unavailable')
    : false;

  return (
    <div className={`group bg-white rounded-xl border-8 border-[#F1B1BA] overflow-hidden hover:shadow-[0_0_30px_rgba(241,177,186,0.5)] transition-all duration-300 flex flex-col h-full shadow-md ${isOutOfStock ? 'opacity-50' : ''}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-white flex items-center justify-center p-4">
        {/* Background Fallback Icon */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-100 pointer-events-none">
          <i className="fas fa-broom text-6xl"></i>
        </div>

        {loading && !imgError && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <div className="w-6 h-6 border-2 border-[#F1B1BA] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        
        {imgError ? (
          <div className="flex flex-col items-center justify-center text-slate-300 py-8">
            <i className="fas fa-image text-3xl mb-2 opacity-50"></i>
            <span className="text-[10px] font-bold uppercase tracking-widest">Image Unavailable</span>
          </div>
        ) : (
          <img 
            src={product.image} 
            alt={product.name} 
            onLoad={() => setLoading(false)}
            onError={(e) => {
              console.warn(`Failed to load image: ${product.image}`);
              setImgError(true);
              setLoading(false);
            }}
            className={`max-w-full max-h-full object-contain transition-all duration-700 ${loading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'} group-hover:scale-105`}
          />
        )}

        {/* Quick View Overlay Button (Desktop) */}
        <div className="absolute inset-0 bg-[#5C3E43]/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10">
          <button 
            onClick={() => onQuickView(product)}
            className="bg-white text-[#5C3E43] px-4 py-2 rounded-full font-black text-[10px] uppercase tracking-widest shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 hover:bg-[#F1B1BA] hover:text-[#5C3E43]"
          >
            Explore Item
          </button>
        </div>
      </div>
      
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest truncate">{product.category}</span>
        </div>
        
        <h3 className="text-sm font-black text-slate-900 mb-4 leading-tight group-hover:text-[#F1B1BA] transition-colors line-clamp-2 h-10">
          {product.name}
        </h3>
        
        {product.asin && (
          <div className="flex items-center justify-between mb-4 bg-[#F1B1BA]/5 p-2.5 rounded-xl border border-[#F1B1BA]/10">
            <div>
              <span className="block text-[8px] font-black text-[#5C3E43]/60 uppercase tracking-widest leading-none mb-1">
                Price
              </span>
              <span className="text-xs font-black text-[#5C3E43]">
                {liveData?.price || `£${product.price.toFixed(2)}`}
              </span>
              {liveData?.soldByAmazon === false && (
                <span className="block text-[8px] text-slate-400 mt-0.5 font-medium italic">
                  Sold by third-party seller on Amazon
                </span>
              )}
            </div>
            {liveData?.availability && (
              <div className="text-right">
                <span className="block text-[8px] font-black text-[#5C3E43]/60 uppercase tracking-widest leading-none mb-1">
                  Availability
                </span>
                <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${
                  isOutOfStock 
                    ? 'bg-red-50 text-red-600 border border-red-200' 
                    : liveData.availability.toLowerCase().includes('only')
                      ? 'bg-amber-50 text-amber-600 border border-amber-200'
                      : 'bg-green-50 text-green-600 border border-green-200'
                }`}>
                  {liveData.availability}
                </span>
              </div>
            )}
          </div>
        )}
        
        <div className="flex items-center justify-center mt-auto pt-4 border-t border-slate-50 gap-2">
          <div className="flex gap-2 w-full">
            <button 
              onClick={() => onQuickView(product)}
              className="flex-1 text-[10px] font-black bg-slate-50 text-[#5C3E43] px-3 py-2.5 rounded-lg hover:bg-slate-100 border border-slate-200 transition-all uppercase tracking-wider flex items-center justify-center whitespace-nowrap"
            >
              View Details
            </button>
            <a 
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                const targetAsin = product.asin || product.id;
                if (targetAsin) {
                  fetch('/api/track-click', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ asin: targetAsin })
                  }).catch((err) => console.error('Error tracking click:', err));
                }
              }}
              className="flex-[1.5] flex items-center justify-center text-[10px] font-black bg-[#F1B1BA] text-[#5C3E43] px-3 py-2.5 rounded-lg hover:bg-[#D498A1] transition-all uppercase tracking-wider shadow-sm hover:shadow-md active:scale-95 text-center whitespace-nowrap"
            >
              Buy on {product.company || (
                product.url.includes('amazon') || product.url.includes('amzlink') || product.url.includes('amzn.to') ? 'Amazon' :
                product.url.includes('shopmy') ? 'ShopMy' :
                product.url.includes('scampanddude') ? 'Scamp & Dude' :
                product.url.includes('bonmarche') ? 'Bonmarché' :
                product.url.includes('thecommense') ? 'Commense' :
                product.url.includes('marksandspencer') ? 'M&S' : 'Store'
              )}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;